import { useCallback, useRef, useEffect } from 'react';
import { calculateAutoLayout } from '../utils/autoLayoutUtils';
import {
  isMissionToSidebarDragActive,
  cancelMissionToSidebarDrag,
  restoreFlowchartCanvasPointerEvents,
} from '../../../../../utils/missionToSidebarBridge';
import { useFlowchartDragState } from './useFlowchartDragState';
import { useFlowchartDropTarget } from './useFlowchartDropTarget';
import { useFlowchartDragStart } from './useFlowchartDragStart';
import { useFlowchartDragMove } from './useFlowchartDragMove';
import { useFlowchartDragComplete } from './useFlowchartDragComplete';

/**
 * Hook for managing node dragging (single and multi-select drag)
 *
 * PERFORMANCE:
 * - Uses refs + direct DOM transforms for the actively dragged nodes so movement stays smooth.
 * - Does not recompute graph/edge paths during drag; large campaigns stay responsive and
 *   paths recompute once on drop.
 * - Debounces drop target detection independently because it scans node geometry.
 */
export function useFlowchartDrag({
  tasks,
  nodePositions,
  nodeDimensions,
  selectedNodes,
  snapToGrid = false,
  autoArrange = false,
  disableDragRef = null,
  onPositionChange,
  onPositionChangeBatch,
  customPositions,
  setCustomPositions,
  isMultiCampaignMode = false,
  campaignId = '',
  getIsInTemporaryMode = null,
  onChainTouched = null,
}) {
  const dragState = useFlowchartDragState(nodeDimensions);

  const { checkDropTarget } = useFlowchartDropTarget({
    tasks,
    nodePositions,
    customPositions,
    nodeDimensions,
    dropTargetRef: dragState.dropTargetRef,
  });

  const { handleNodeDragStart, checkDragThreshold } = useFlowchartDragStart({
    tasks,
    nodePositions,
    customPositions,
    selectedNodes,
    autoArrange,
    disableDragRef,
    campaignId,
    potentialDragNode: dragState.potentialDragNode,
    setPotentialDragNode: dragState.setPotentialDragNode,
    setHasDragged: dragState.setHasDragged,
    setAutoArrangeBlocked: dragState.setAutoArrangeBlocked,
    setDraggingNode: dragState.setDraggingNode,
    draggingNodeRef: dragState.draggingNodeRef,
    dragStartPosRef: dragState.dragStartPosRef,
    dragStartCanvasPosRef: dragState.dragStartCanvasPosRef,
    dragOffsetRef: dragState.dragOffsetRef,
    initialPositionsRef: dragState.initialPositionsRef,
    currentDragPositionsRef: dragState.currentDragPositionsRef,
  });

  const { updateDragPosition } = useFlowchartDragMove({
    snapToGrid,
    customPositions,
    nodePositions,
    selectedNodes,
    isMultiCampaignMode,
    tasks,
    nodeDimensions,
    draggingNodeRef: dragState.draggingNodeRef,
    dragOffsetRef: dragState.dragOffsetRef,
    initialPositionsRef: dragState.initialPositionsRef,
    currentDragPositionsRef: dragState.currentDragPositionsRef,
    dropTargetRef: dragState.dropTargetRef,
    checkDropTarget,
  });

  const { completeDrag } = useFlowchartDragComplete({
    selectedNodes,
    potentialDragNode: dragState.potentialDragNode,
    hasDragged: dragState.hasDragged,
    setPotentialDragNode: dragState.setPotentialDragNode,
    setHasDragged: dragState.setHasDragged,
    setDraggingNode: dragState.setDraggingNode,
    setDropTarget: dragState.setDropTarget,
    draggingNodeRef: dragState.draggingNodeRef,
    dropTargetRef: dragState.dropTargetRef,
    initialPositionsRef: dragState.initialPositionsRef,
    currentDragPositionsRef: dragState.currentDragPositionsRef,
  });

  const handleAutoLayout = useCallback((selectedNodeIds = null) => {
    const newPositions = calculateAutoLayout({
      selectedNodeIds,
      tasks,
      nodePositions,
      customPositions,
      nodeDimensions: dragState.nodeDimensionsRef.current,
    });
    setCustomPositions(newPositions);
  }, [setCustomPositions, tasks, nodePositions, customPositions, dragState.nodeDimensionsRef]);

  const completeDragRef = useRef(completeDrag);
  completeDragRef.current = completeDrag;

  useEffect(() => {
    const onWindowBlur = () => {
      restoreFlowchartCanvasPointerEvents();
      if (dragState.draggingNodeRef.current) {
        completeDragRef.current();
      } else if (isMissionToSidebarDragActive()) {
        cancelMissionToSidebarDrag();
      }
    };

    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('blur', onWindowBlur);
      restoreFlowchartCanvasPointerEvents();
      if (isMissionToSidebarDragActive()) {
        cancelMissionToSidebarDrag();
      }
    };
  }, [dragState.draggingNodeRef]);

  return {
    draggingNode: dragState.draggingNode,
    draggingNodeRef: dragState.draggingNodeRef,
    potentialDragNode: dragState.potentialDragNode,
    hasDragged: dragState.hasDragged,
    isDragging: dragState.draggingNode !== null,
    autoArrangeBlocked: dragState.autoArrangeBlocked,
    dropTarget: dragState.dropTarget,
    dragLayoutTick: dragState.dragLayoutTick,
    currentDragPositionsRef: dragState.currentDragPositionsRef,

    handleNodeDragStart,
    checkDragThreshold,
    updateDragPosition,
    completeDrag,
    handleAutoLayout,
  };
}
